import { useState, useEffect } from 'react';

import styled from 'styled-components';
import moment from 'moment';
import Lottie from 'lottie-react';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import MemberStatusBarChart from './MemberStatusBarChart';
import MemberStatusListTable from './MemberStatusListTable';

import LoadingAnimation from '../../../Animation/NewLoading.json';


export default function MemberStatusBox(props) {
  const {
    allMembers,
    salesData,
    startDate,
    endDate,
    isMonthMode,
    calcDateText,
    selectedPartnerGym,
    isOpenPartnerGymSales,
    isLoadingAllMember,
    isLoading,
    setIsLoading,
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [selectedTab, setSelectedTab] = useState('new');
  const [newMembers, setNewMembers] = useState([]);
  const [reRegisteredMembers, setReRegisteredMembers] = useState([]);
  const [leaveMembers, setLeaveMembers] = useState([]);
  const [aliveMembers, setAliveMembers] = useState([]);
  const [newPrice, setNewPrice] = useState(0);
  const [reRegisteredPrice, setReRegisteredPrice] = useState(0);
  const [monthlyStats, setMonthlyStats] = useState([]);

  // 신규 / 재등록 / 이탈 / 유효 회원 구분 //
  useEffect(() => {
    if (isLoadingAllMember) return;

    const newNewMembers = [];
    const newReRegisteredMembers = [];
    const newAliveMembers = [];
    const newLeaveMembers = [];

    for (let i = 0; i < allMembers.length; i++) {
      let isNew = false;
      let isReRegistered = false;
      let isAlive = false;
      let isLeave = false;

      // 산정기간 내 startDate가 있는 usage 탐색 → 첫 이용권(j===0)이면 신규, 아니면 재등록 //
      for (let j = 0; j < allMembers[i].usages.length; j++) {
        if (startDate.isSameOrBefore(allMembers[i].usages[j].startDate, 'day')
          && endDate.isSameOrAfter(allMembers[i].usages[j].startDate, 'day')) {
          allMembers[i].startDate = allMembers[i].usages[j].startDate;
          if (j === 0) isNew = true;
          else isReRegistered = true;
          break;
        }
      }

      // 유효 회원: 산정기간 내 1일이라도 유효했던 회원 (overlap 체크) //
      for (let j = 0; j < allMembers[i].usages.length; j++) {
        if (moment(allMembers[i].usages[j].startDate).isSameOrBefore(endDate, 'day')
          && moment(allMembers[i].usages[j].endDate).isSameOrAfter(startDate, 'day')) {
          isAlive = true;
          break;
        }
      }

      // 이탈 회원: 마지막 이용권의 endDate가 산정기간 내이고 오늘 이전인 회원 //
      const today = moment();

      for (let j = 0; j < allMembers[i].usages.length; j++) {
        if (j === allMembers[i].usages.length - 1
          && startDate.isSameOrBefore(allMembers[i].usages[j].endDate, 'day')
          && endDate.isSameOrAfter(allMembers[i].usages[j].endDate, 'day')
          && today.isAfter(allMembers[i].usages[j].endDate, 'day')) {
          allMembers[i].endDate = allMembers[i].usages[j].endDate;
          isLeave = true;
          break;
        }
      }

      if (isNew) newNewMembers.push(allMembers[i]);
      if (isReRegistered) newReRegisteredMembers.push(allMembers[i]);
      if (isAlive) newAliveMembers.push(allMembers[i]);
      if (isLeave) newLeaveMembers.push(allMembers[i]);
    }

    // 신규/재등록: 최근 등록일 순 정렬 //
    const sortByStartDateDesc = (a, b) => {
      if (a.startDate < b.startDate) return 1;
      if (a.startDate > b.startDate) return -1;
      return 0;
    };

    newNewMembers.sort(sortByStartDateDesc);
    newReRegisteredMembers.sort(sortByStartDateDesc);

    // 이탈 회원: 최근 이탈일 순 정렬 //
    newLeaveMembers.sort((a, b) => {
      if (a.endDate < b.endDate) return 1;
      if (a.endDate > b.endDate) return -1;
      return 0;
    });

    setNewMembers(newNewMembers);
    setReRegisteredMembers(newReRegisteredMembers);
    setAliveMembers(newAliveMembers);
    setLeaveMembers(newLeaveMembers);

    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, [startDate, endDate, allMembers, isLoadingAllMember]);

  // 신규 / 재등록 회원 매출 계산 //
  useEffect(() => {
    if (salesData.length === 0 || allMembers.length === 0) return;

    const newMembersList = [];
    const reRegisteredMembersList = [];
    let calculatedNewPrice = 0;
    let calculatedReRegisteredPrice = 0;

    // 산정기간 내 approvalDate가 있는 usage가 첫 이용권이면 신규, 아니면 재등록 //
    for (let i = 0; i < allMembers.length; i++) {
      for (let j = 0; j < allMembers[i].usages.length; j++) {
        if (startDate.isSameOrBefore(allMembers[i].usages[j].approvalDate, 'day')
          && endDate.isSameOrAfter(allMembers[i].usages[j].approvalDate, 'day')) {
          if (j === 0) newMembersList.push(allMembers[i]);
          else reRegisteredMembersList.push(allMembers[i]);
          break;
        }
      }
    }

    // memberId 기반 O(1) lookup 위해 Set 변환 //
    const newMemberIds = new Set(newMembersList.map(m => m.memberId));
    const reRegisteredMemberIds = new Set(reRegisteredMembersList.map(m => m.memberId));

    for (let i = 0; i < salesData.length; i++) {
      const totalPrice = salesData[i].cardPrice + salesData[i].cashPrice;

      if (newMemberIds.has(salesData[i].memberId)) calculatedNewPrice += totalPrice;
      if (reRegisteredMemberIds.has(salesData[i].memberId)) calculatedReRegisteredPrice += totalPrice;
    }

    setNewPrice(calculatedNewPrice);
    setReRegisteredPrice(calculatedReRegisteredPrice);
  }, [salesData, allMembers]);

  // 산정기간 내 월별 신규 / 재등록 / 이탈 집계 //
  useEffect(() => {
    if (isLoadingAllMember) return;

    // 월 목록 생성: 월별 모드는 현재 보고있는 월 기준 최근 9개월, 그 외는 산정기간 전체 //
    const months = [];

    if (isMonthMode) {
      const anchor = endDate.clone().startOf('month');
      const cursor = anchor.clone().subtract(8, 'month');

      while (cursor.isSameOrBefore(anchor, 'month')) {
        months.push(cursor.clone());
        cursor.add(1, 'month');
      }
    }
    else {
      const cursor = startDate.clone().startOf('month');
      const lastMonth = endDate.clone().startOf('month');

      while (cursor.isSameOrBefore(lastMonth, 'month')) {
        months.push(cursor.clone());
        cursor.add(1, 'month');
      }
    }

    const nowMonthKey = moment().format('YYYY-MM');

    const stats = months.map((month) => ({
      label: month.format('YY.M'),
      isNow: month.format('YYYY-MM') === nowMonthKey,
      new: 0,
      renewal: 0,
      churn: 0,
      net: 0,
    }));

    // 월 키 기반 O(1) 인덱스 lookup //
    const monthIndexMap = new Map();

    for (let i = 0; i < months.length; i++) monthIndexMap.set(months[i].format('YYYY-MM'), i);

    const today = moment();

    for (let i = 0; i < allMembers.length; i++) {
      const usages = allMembers[i].usages;

      if (usages.length === 0) continue;

      // 이용권 시작월 기준 신규(첫 이용권) / 재등록(이후 이용권) 카운트 //
      for (let j = 0; j < usages.length; j++) {
        const startIndex = monthIndexMap.get(moment(usages[j].startDate).format('YYYY-MM'));

        if (startIndex === undefined) continue;

        if (j === 0) stats[startIndex].new += 1;
        else stats[startIndex].renewal += 1;
      }

      // 마지막 이용권 종료월이 오늘 이전이면 해당 월 이탈 카운트 //
      const lastUsage = usages[usages.length - 1];
      const churnIndex = monthIndexMap.get(moment(lastUsage.endDate).format('YYYY-MM'));

      if (churnIndex !== undefined && today.isAfter(lastUsage.endDate, 'day')) stats[churnIndex].churn += 1;
    }

    for (let i = 0; i < stats.length; i++) stats[i].net = stats[i].new + stats[i].renewal - stats[i].churn;

    setMonthlyStats(stats);
  }, [startDate, endDate, isMonthMode, allMembers, isLoadingAllMember]);

  // 회원 클릭시 상세 페이지 이동 //
  function onClickMember(memberId) {
    if (selectedPartnerGym === undefined || selectedPartnerGym.isMyStore) {
      window.open('/member/valid/detail/' + memberId, '_blank');
    }
    else {
      window.open('/member/partner_gym/detail/' + memberId, '_blank');
    }
  }

  // 현재 탭에 따른 데이터 //
  const isNewTab = selectedTab === 'new';
  const isReRegisteredTab = selectedTab === 'reregistered';
  const isChurnedTab = selectedTab === 'churned';

  let currentMembers;
  let currentPrice;
  let accentColor;
  let salesLabel;
  let accentColorHover;
  let rateLabel;
  let stateLabel;
  let listTitle;
  let memberType;

  if (isNewTab) {
    currentMembers = newMembers;
    currentPrice = newPrice;
    accentColor = theme.brand500;
    accentColorHover = theme.brand500Alpha50;
    salesLabel = t('statistics.memberStatus.newSales');
    rateLabel = t('statistics.memberStatus.newRate');
    stateLabel = t('statistics.memberStatus.new');
    listTitle = t('statistics.memberStatus.newList');
    memberType = 'new';
  }
  else if (isReRegisteredTab) {
    currentMembers = reRegisteredMembers;
    currentPrice = reRegisteredPrice;
    accentColor = theme.webBrand500;
    accentColorHover = theme.webBrand600;
    salesLabel = t('statistics.memberStatus.reRegisteredSales');
    rateLabel = t('statistics.memberStatus.reRegisteredRate');
    stateLabel = t('statistics.memberStatus.reRegistered');
    listTitle = t('statistics.memberStatus.reRegisteredList');
    memberType = 'reregistered';
  }
  else {
    currentMembers = leaveMembers;
    currentPrice = 0;
    accentColor = isDark ? theme.red600 : theme.red200;
    accentColorHover = isDark ? theme.red600 : theme.red200;
    salesLabel = '';
    rateLabel = t('statistics.memberStatus.churnRate');
    stateLabel = t('statistics.memberStatus.churned');
    listTitle = t('statistics.memberStatus.churnedList');
    memberType = 'churned';
  }

  let currentRate = 0;

  if (aliveMembers.length > 0) currentRate = Math.round((currentMembers.length / aliveMembers.length) * 100);

  // 순증감 회원수: 신규 + 재등록 - 이탈 //
  const netMemberCount = newMembers.length + reRegisteredMembers.length - leaveMembers.length;

  return (
    <TopLevelWrapper $isDark={isDark}>
      {
        isLoading ?
          <AnimationBox>
            <LottieWrapper>
              <Lottie animationData={LoadingAnimation} loop={true} />
            </LottieWrapper>
          </AnimationBox>
          :
          !isOpenPartnerGymSales ?
            <NotOpenWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={15} color={theme.subText} cursor='default'>{t('statistics.partnerGymPrivate')}</Text>
              <Text fontFamily='NotoSansKR-Regular' fontSize={15} color={theme.subText} cursor='default'>{t('statistics.partnerGymPrivateDesc')}</Text>
            </NotOpenWrapper>
            :
            <MainContentWrapper>
              <HeaderWrapper>
                <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text} cursor='default'>{calcDateText()}</Text>
                <RowWrapper $gap={5}>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text} cursor='default'>
                    {t('statistics.memberStatus.memberCount')}
                  </Text>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={netMemberCount >= 0 ? theme.brand500 : theme.pointRed} cursor='default'>
                    {netMemberCount > 0 ? '+' : ''}{netMemberCount}
                  </Text>
                </RowWrapper>
              </HeaderWrapper>

              {/* 상단 정보 행 */}
              <InfoRow>
                <InfoLeft>
                  {
                    isChurnedTab ?
                      <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text} cursor='default'>
                        {t('statistics.memberStatus.churnedMembers')}
                      </Text>
                      :
                      <RowWrapper $gap={10}>
                        <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text} cursor='default'>
                          {salesLabel}
                        </Text>
                        <RowWrapper $gap={5}>
                          <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text} cursor='default'>
                            {currentPrice.toLocaleString('ko-KR')}
                          </Text>
                          <Text fontFamily='NotoSansKR-Regular' fontSize={20} color={theme.subText} cursor='default'>
                            {t('statistics.won')}
                          </Text>
                        </RowWrapper>
                      </RowWrapper>
                  }
                </InfoLeft>
                <InfoRight>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text} cursor='default'>
                    {rateLabel}
                  </Text>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={accentColor} cursor='default'>
                    {currentRate}%
                  </Text>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={accentColor} cursor='default'>
                    {'[ ' + t('statistics.memberMovement.total') + ' ' + currentMembers.length + t('statistics.personUnit') + ' ' + stateLabel + ' ]'}
                  </Text>
                </InfoRight>
              </InfoRow>

              {/* 메인 컨텐츠 영역 */}
              <ContentRow>
                {/* 왼쪽: 월별 신규/재등록/이탈 막대 차트 */}
                <LeftColumn>
                  <MemberStatusBarChart
                    monthlyStats={monthlyStats}
                    newColor={theme.brand500}
                    renewalColor={theme.webBrand500}
                    churnColor={isDark ? theme.red600 : theme.red200}
                    scrollBarColor={accentColor} />
                </LeftColumn>

                {/* 오른쪽: 회원 목록 + 탭 토글 */}
                <RightColumn>
                  <ListTitleRow>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='default'>
                      {listTitle}
                    </Text>
                    <ToggleWrapper>
                      <ToggleButton
                        $isActive={isNewTab}
                        $activeColor={theme.brand500}
                        onClick={() => setSelectedTab('new')}>
                        <Text fontFamily='NotoSansKR-Medium' fontSize={15} color={isNewTab ? theme.subBackground : theme.text} cursor='pointer'>
                          {t('statistics.memberStatus.tabNew')}
                        </Text>
                      </ToggleButton>
                      <ToggleButton
                        $isActive={isReRegisteredTab}
                        $activeColor={theme.webBrand500}
                        onClick={() => setSelectedTab('reregistered')}>
                        <Text fontFamily='NotoSansKR-Medium' fontSize={15} color={isReRegisteredTab ? theme.subBackground : theme.text} cursor='pointer'>
                          {t('statistics.memberStatus.tabReRegistered')}
                        </Text>
                      </ToggleButton>
                      <ToggleButton
                        $isActive={isChurnedTab}
                        $activeColor={isDark ? theme.red600 : theme.red200}
                        onClick={() => setSelectedTab('churned')}>
                        <Text fontFamily='NotoSansKR-Medium' fontSize={15} color={isChurnedTab ? theme.subBackground : theme.text} cursor='pointer'>
                          {t('statistics.memberStatus.tabChurned')}
                        </Text>
                      </ToggleButton>
                    </ToggleWrapper>
                  </ListTitleRow>

                  <MemberStatusListTable
                    members={currentMembers}
                    memberType={memberType}
                    accentColor={accentColor}
                    accentColorHover={accentColorHover}
                    onClickMember={onClickMember} />
                </RightColumn>

              </ContentRow>
            </MainContentWrapper>
      }
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  margin: 39.5px 0;
  padding: 20px 40px;

  width: 100%;
  max-width: 960px;
  height: 450px;

  gap: 10px;

  border-radius: 20px;
  background-color: ${props => props.theme.subBackground};
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const MainContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  width: 100%;

  gap: 10px;
`;

const AnimationBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 448px;

  z-index: 5;

  background-color: ${props => props.theme.subBackground};
  border-radius: 20px;
`;

const LottieWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 160px;
  height: 160px;
`;

const NotOpenWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 448px;
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const InfoLeft = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
`;

const InfoRight = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 10px;
`;

const RowWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: ${props => props.$gap}px;
`;

const ContentRow = styled.div`
  display: flex;
  flex-direction: row;

  gap: 30px;

  width: 100%;
  height: 360px;
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex: 0 0 420px;
  width: 420px;

  padding: 0 20px;

  box-sizing: border-box;

  overflow: clip;
`;

const ToggleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  padding: 5px;

  gap: 5px;

  height: 41px;

  border-radius: 8px;
  background-color: ${props => props.theme.buttonFill};

  box-sizing: border-box;
`;

const ToggleButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 90px;
  height: 29px;

  border-radius: 6px;
  background-color: ${props => props.$isActive ? props.$activeColor : 'transparent'};
  box-shadow: ${props => props.$isActive ? '0px 9px 7.1px 0px rgba(0, 0, 0, 0.06)' : 'none'};

  cursor: pointer;

  transition: all 0.3s ease-in-out;
`;

/* 오른쪽 회원 목록 영역 */
const RightColumn = styled.div`
  display: flex;
  flex-direction: column;

  gap: 10px;

  width: 480px;
`;

const ListTitleRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding-top: 20px;

  width: 100%;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  white-space: nowrap;

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: ${props => props.cursor};
  }
`;
